import type { AuditEvent } from "../../../models/audit/auditEvent";

const hasText = (value: string | null | undefined): value is string =>
  !!value && value.trim().length > 0;

/**
 * Resolves the actor shown on an audit event row — *who asked for this*, not
 * what carried it out.
 *
 * The distinction matters for worker-emitted run events: they record
 * `performedBy: "ai_system"` because the agent did the work, but the run was
 * triggered by a person, whose id is on `triggeredByUserId`. Attributing
 * those to "AI" hides the human in the audit trail, so the triggering user
 * always wins when one exists; `AuditEventDetailPane` surfaces the automated
 * execution separately.
 *
 * Worker events carry no `triggeredByEmail` (`Users` is not importable from
 * `midship_core`), hence the `memberEmailById` lookup.
 */
export function resolveActor(
  event: Pick<
    AuditEvent,
    "triggeredByEmail" | "triggeredByUserId" | "performedBy"
  >,
  memberEmailById?: (userId: string) => string | undefined,
): string {
  if (hasText(event.triggeredByEmail)) {
    return event.triggeredByEmail;
  }
  if (hasText(event.triggeredByUserId)) {
    const memberEmail = memberEmailById?.(event.triggeredByUserId);
    return hasText(memberEmail) ? memberEmail : "Unknown user";
  }
  return event.performedBy === "ai_system" ? "AI" : "Unknown";
}

/**
 * Whether the agent executed an event that a person triggered — the case that
 * warrants showing both, rather than crediting one and dropping the other.
 */
export function isAutomatedExecution(
  event: Pick<AuditEvent, "performedBy" | "triggeredByUserId">,
): boolean {
  return event.performedBy === "ai_system" && hasText(event.triggeredByUserId);
}
